import { api } from '../http';
import { AxiosResponse } from 'axios';
import { DefaultRes } from '../types';
import { COLORS } from '../pages/app/reports/groups-teams-activity/constants';
import {
    GroupRow,
    TeamRow,
    UserRow,
    WeeklyDataPoint,
    FeatureUsagePoint,
    TagOption,
} from '../pages/app/reports/groups-teams-activity/types';

// ─── Response types from API ────────────────────────────

interface GroupsResponse {
    groups: GroupRow[];
    noGroup: GroupRow | null;
    summary: {
        groups: number;
        teams: number;
        totalUsers: number;
        active: number;
        inactive: number;
    };
}

interface TeamsResponse {
    teams: TeamRow[];
    summary: {
        teams: number;
        users: number;
        active: number;
        inactive: number;
        activePercent: number;
    };
}

interface UsersResponse {
    users: UserRow[];
    summary: {
        active: number;
        inactive: number;
        activeRate: number;
        total: number;
    };
}

interface FeatureUsageRaw {
    feature: string;
    count: number;
}

// ─── Feature color mapping ──────────────────────────────

const FEATURE_COLOR_MAP: Record<string, string> = {
    'Check On Me': COLORS.featureCheckOnMe,
    'Field Safety Reports': COLORS.featureSafetyReports,
    'Fake Call': COLORS.featureFakeCall,
    'Risk Reviews': COLORS.featureRiskReviews,
    'SOS Alert': COLORS.featureSOS,
};

const FEATURE_LABEL_MAP: Record<string, string> = {
    'Check On Me': 'Check\nOn Me',
    'Field Safety Reports': 'Field Safety\nReports',
    'Fake Call': 'Fake\nCall',
    'Risk Reviews': 'Risk\nReviews',
    'SOS Alert': 'SOS\nAlert',
};

// ─── Service ────────────────────────────────────────────

export class DashboardApiService {

    /**
     * Build query string params for time period and tags.
     */
    private static buildParams(
        period: string,
        customStartDate?: string | null,
        customEndDate?: string | null,
        selectedTags?: string[],
        selectedUserTags?: string[]
    ): Record<string, string> {
        const params: Record<string, string> = { period };
        if (period === 'custom' && customStartDate && customEndDate) {
            params.startDate = customStartDate;
            params.endDate = customEndDate;
        }
        if (selectedTags && selectedTags.length > 0) {
            params.tags = selectedTags.join(',');
        }
        if (selectedUserTags && selectedUserTags.length > 0) {
            params.userTags = selectedUserTags.join(',');
        }
        return params;
    }

    /**
     * Level 1: Fetch all groups with summary.
     */
    static async fetchGroups(
        period: string,
        customStartDate?: string | null,
        customEndDate?: string | null,
        selectedTags?: string[],
        selectedUserTags?: string[]
    ): Promise<GroupsResponse> {
        const params = this.buildParams(period, customStartDate, customEndDate, selectedTags, selectedUserTags);

        const { data } = await api.get<
            DefaultRes<GroupsResponse>,
            AxiosResponse<DefaultRes<GroupsResponse>>
        >('dashboard/groups', { params });

        return data.result;
    }

    /**
     * Level 2: Fetch all teams in a group.
     */
    static async fetchTeams(
        groupId: string,
        period: string,
        customStartDate?: string | null,
        customEndDate?: string | null,
        selectedTags?: string[],
        selectedUserTags?: string[]
    ): Promise<TeamsResponse> {
        const params = this.buildParams(period, customStartDate, customEndDate, selectedTags, selectedUserTags);

        const { data } = await api.get<
            DefaultRes<TeamsResponse>,
            AxiosResponse<DefaultRes<TeamsResponse>>
        >(`dashboard/groups/${groupId}/teams`, { params });

        return data.result;
    }

    /**
     * Level 3: Fetch all users in a team.
     */
    static async fetchUsers(
        teamId: string,
        period: string,
        customStartDate?: string | null,
        customEndDate?: string | null,
    ): Promise<UsersResponse> {
        const params = this.buildParams(period, customStartDate, customEndDate);

        const { data } = await api.get<
            DefaultRes<UsersResponse>,
            AxiosResponse<DefaultRes<UsersResponse>>
        >(`dashboard/teams/${teamId}/users`, { params });

        return data.result;
    }

    /**
     * Fetch weekly active rate chart data.
     */
    static async fetchWeeklyActive(
        scopeType: 'all' | 'group' | 'team',
        scopeId?: string
    ): Promise<WeeklyDataPoint[]> {
        const params: Record<string, string> = { scopeType };
        if (scopeId) params.scopeId = scopeId;

        const { data } = await api.get<
            DefaultRes<WeeklyDataPoint[]>,
            AxiosResponse<DefaultRes<WeeklyDataPoint[]>>
        >('dashboard/weekly-active', { params });

        return data.result;
    }

    /**
     * Fetch feature usage chart data.
     */
    static async fetchFeatureUsage(
        period: string,
        scopeType: 'all' | 'group' | 'team',
        scopeId?: string,
        customStartDate?: string | null,
        customEndDate?: string | null,
    ): Promise<FeatureUsagePoint[]> {
        const params: Record<string, string> = { period, scopeType };
        if (scopeId) params.scopeId = scopeId;
        if (period === 'custom' && customStartDate && customEndDate) {
            params.startDate = customStartDate;
            params.endDate = customEndDate;
        }

        const { data } = await api.get<
            DefaultRes<FeatureUsageRaw[]>,
            AxiosResponse<DefaultRes<FeatureUsageRaw[]>>
        >('dashboard/feature-usage', { params });

        // Map to display format with colors and label line breaks
        return data.result.map((item) => ({
            feature: FEATURE_LABEL_MAP[item.feature] || item.feature,
            count: item.count,
            color: FEATURE_COLOR_MAP[item.feature] || COLORS.primary,
        }));
    }

    /**
     * Fetch tag options for the filter picker.
     * Cached per-type for TAG_OPTIONS_TTL_MS so navigating in and out of a
     * list page doesn't hit the endpoint on every mount. In-flight requests
     * are deduped via the promise cache.
     */
    static async fetchTagOptions(type: 'team' | 'user'): Promise<TagOption[]> {
        const now = Date.now();
        const cached = tagOptionsCache[type];
        if (cached && now - cached.at < TAG_OPTIONS_TTL_MS) {
            return cached.promise;
        }
        const promise = (async () => {
            try {
                const { data } = await api.get<
                    DefaultRes<TagOption[]>,
                    AxiosResponse<DefaultRes<TagOption[]>>
                >('dashboard/tag-options', { params: { type } });
                return data.result;
            } catch (err) {
                // Invalidate on failure so the next caller retries instead
                // of being stuck with a rejected cached promise.
                delete tagOptionsCache[type];
                throw err;
            }
        })();
        tagOptionsCache[type] = { promise, at: now };
        return promise;
    }
}

// ─── Module-level tag options cache ─────────────────────────
const TAG_OPTIONS_TTL_MS = 300_000;
const tagOptionsCache: Partial<Record<'team' | 'user', { promise: Promise<TagOption[]>; at: number }>> = {};
